/**
 * AI Assistant Action Types
 * Constants for AI assistant functionality across the application
 */

export const AI_ASSISTANT_ACTIONS = {
    SUGGEST: 'suggest',
    GRAMMAR: 'grammar',
    TRANSLATE: 'translate',
    SUMMARIZE: 'summarize',
    IMPROVE: 'improve',
    VALIDATE_FORMAT: 'validate_format',
    ENHANCE: 'enhance',
    CUSTOM: 'custom',
    ENTITY_SUMMARY: 'entity_summary',
    PLAN_NEXT_STEPS: 'plan_next_steps',
    RECENT_UPDATES: 'recent_updates',
} as const;

export type AiAssistantAction = (typeof AI_ASSISTANT_ACTIONS)[keyof typeof AI_ASSISTANT_ACTIONS];

/**
 * Array of all AI assistant action keys for easy iteration
 */
export const AI_ASSISTANT_ACTION_KEYS = Object.values(AI_ASSISTANT_ACTIONS);

/** Provider values recorded by LLM observability across chat, embedding, and reranking. */
export const AI_OBSERVABILITY_PROVIDERS = ['OPENAI', 'GOOGLE', 'ANTHROPIC', 'ZEROENTROPY', 'COHERE'] as const;

/**
 * AI provider options for forms and displays. ZeroEntropy is retired but stays
 * listed so historical pricing rows and LLM request logs keep their label.
 */
export const AI_PROVIDERS = [
    { label: 'OpenAI', value: 'OPENAI' },
    { label: 'Google', value: 'GOOGLE' },
    { label: 'Anthropic', value: 'ANTHROPIC' },
    { label: 'ZeroEntropy', value: 'ZEROENTROPY' },
    { label: 'Cohere', value: 'COHERE' },
];

/**
 * AI Prompt Provider values as tuple for Zod enum validation.
 *
 * Mirrors the full `AiPromptProvider` Prisma enum so the validation tuple
 * matches the column. COHERE exists in the column for AiModelPricing (rerank
 * cost rows); ZEROENTROPY is retired and remains only for historical pricing
 * and usage rows. Both are excluded from the chat-prompt editor's provider
 * dropdown (see client/src/components/ai-prompts/constants.ts), model tiers
 * and AI tier settings reject them, and chat/LLM execution rejects both in
 * getAiSdkModel.
 */
export const AI_PROMPT_PROVIDERS = AI_OBSERVABILITY_PROVIDERS;
export type AiPromptProviderType = (typeof AI_PROMPT_PROVIDERS)[number];

/** Maximum number of ordered fallback models configured for a model tier. */
export const MAX_AI_MODEL_TIER_FALLBACKS = 3;

/**
 * Reasoning effort levels for prompts and providers, ordered cheapest → most thorough.
 *
 * Mirrors the ladder the installed `@ai-sdk/openai` accepts for `providerOptions.openai
 * .reasoningEffort` (GPT-5.6 and friends document `none, low, medium, high, xhigh, max`).
 * `none` is deliberately absent: it is the one level a chunk of the fleet rejects outright
 * (gpt-5-nano 400s on it), and "no reasoning at all" is already expressible via a
 * non-reasoning tier. Not every model accepts every level either — gpt-5.4-mini rejects
 * `minimal`. The OpenAI provider boundary handles the new top rungs model-by-model; other
 * compatibility errors continue to surface from the provider.
 */
export const REASONING_EFFORTS = {
    MINIMAL: 'minimal',
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    XHIGH: 'xhigh',
    MAX: 'max',
} as const;

export type ReasoningEffort = (typeof REASONING_EFFORTS)[keyof typeof REASONING_EFFORTS];

/**
 * Resolve the shared effort ladder for one OpenAI model attempt.
 *
 * GPT-5.6-class models accept `xhigh` and `max`, and so does GPT-6 and later. GPT-6 dropped
 * `minimal` from its ladder (none, low, medium, high, xhigh, max), so `minimal` rounds up to
 * `low` there. Older reasoning families retain the operator's intent at their highest supported
 * rung (`high`), while non-reasoning models drop top-rung effort entirely. This resolver belongs
 * at the attempt boundary because a single tier setting can reach several differently capable
 * fallback models.
 */
export function resolveOpenAiReasoningEffort(
    model: string,
    effort: ReasoningEffort | null | undefined,
): ReasoningEffort | undefined {
    if (effort === null || effort === undefined) return undefined;

    const isGpt6OrLater = /^gpt-(?:[6-9]|[1-9]\d)/i.test(model);
    if (isGpt6OrLater) return effort === REASONING_EFFORTS.MINIMAL ? REASONING_EFFORTS.LOW : effort;

    const isTopRung = effort === REASONING_EFFORTS.XHIGH || effort === REASONING_EFFORTS.MAX;
    if (!isTopRung || /^gpt-5\.6(?:$|-)/i.test(model)) return effort;

    if (/^(?:o\d|gpt-5)/i.test(model)) return REASONING_EFFORTS.HIGH;
    return undefined;
}

/** Anthropic's `output_config.effort` tiers. */
export type AnthropicEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

/**
 * Map the operator-facing reasoning effort onto Anthropic's `output_config.effort` tiers.
 * 'minimal' collapses to Anthropic's lowest tier, and 'xhigh'/'max' clamp to 'high' on models
 * whose effort ladder stops there (see `anthropicSupportsTopEffort`) — the effort is shared
 * across an attempt chain, so an OpenAI-driven top rung must degrade on an Anthropic fallback
 * rather than 400 it. Unknown values return undefined so a config typo falls back to the model
 * default rather than a request error.
 */
export function mapReasoningEffortToAnthropic(effort: string, model: string): AnthropicEffort | undefined {
    if (effort === 'minimal' || effort === 'low') return 'low';
    if (effort === 'medium' || effort === 'high') return effort;
    if (effort === 'xhigh' || effort === 'max') return anthropicSupportsTopEffort(model) ? effort : 'high';
    return undefined;
}

/**
 * Anthropic reasoning capabilities come in three generations, and sending the wrong control
 * is a request-level 400 (`invalid_request_error`), not a warning — the provider adapter
 * forwards `providerOptions.anthropic.thinking`/`effort` to the wire verbatim. The
 * generations (mirrors `getModelCapabilities` in the installed `@ai-sdk/anthropic`, which the
 * SDK itself uses to decide effort-vs-budget for its top-level `reasoning` option):
 *
 * - **adaptive/effort models** (Opus 4.6+, Sonnet 4.6+, Opus/Sonnet/Fable 5): accept
 *   `output_config.effort` and `thinking: {disabled}`; reject `thinking.budget_tokens` —
 *   EXCEPT Sonnet 4.6, which uniquely still accepts `thinking: {enabled, budget_tokens}`
 *   (the installed provider's `thinking` union: "for models before Opus 4.6, except
 *   Sonnet 4.6 still supports it").
 * - **budget-thinking models** (Claude 3.7 → the 4.0–4.5 families): accept
 *   `thinking: {enabled, budget_tokens}`; reject `effort` — EXCEPT Opus 4.5, where effort
 *   launched one generation before adaptive thinking, so it accepts both budget and effort.
 * - **pre-thinking models** (Claude 3 / 3.5): reject both.
 *
 * Config that a model cannot express is DROPPED with a log — the tier/prompt params are shared
 * across the whole fallback chain, so a budget tuned for a Google primary must not 400 an
 * Anthropic fallback. (A positive budget additionally never crosses providers at all — see
 * `buildProviderOptionsForAttempt`'s owner scoping — so these guards are the
 * same-provider defence: an Anthropic-primary budget hitting an adaptive Anthropic fallback.)
 */
const ANTHROPIC_ADAPTIVE_THINKING_MODELS = [
    'claude-opus-5',
    'claude-fable-5',
    'claude-sonnet-5',
    'claude-opus-4-8',
    'claude-opus-4-7',
    'claude-opus-4-6',
    'claude-sonnet-4-6',
] as const;

export function isAnthropicAdaptiveThinkingModel(model: string): boolean {
    return ANTHROPIC_ADAPTIVE_THINKING_MODELS.some((m) => model.includes(m));
}

/**
 * `output_config.effort` support is a superset of adaptive thinking: it launched with Opus 4.5,
 * one generation before adaptive thinking (Opus/Sonnet 4.6). See Anthropic's effort docs.
 */
export function anthropicSupportsEffort(model: string): boolean {
    return isAnthropicAdaptiveThinkingModel(model) || model.includes('claude-opus-4-5');
}

/**
 * Models the provider documents as accepting `xhigh`. Mirrors `supportsXhighEffort` in the
 * installed `@ai-sdk/anthropic` capability table; the 4.6 generation and Opus 4.5 are excluded.
 *
 * Effort is NOT provider-scoped, so a top rung configured for an OpenAI primary reaches every
 * Anthropic fallback in the chain. Off this list both rungs clamp to 'high': `xhigh` would be
 * rejected outright, and `max` is clamped by choice rather than necessity — the SDK's own map
 * (`xhigh: supportsXhighEffort ? 'xhigh' : 'max'`) implies adaptive 4.6 models do accept `max`,
 * but silently promoting a fallback past the primary's rung is not worth the cost surprise.
 */
const ANTHROPIC_TOP_EFFORT_MODELS = [
    'claude-opus-5',
    'claude-fable-5',
    'claude-sonnet-5',
    'claude-opus-4-8',
    'claude-opus-4-7',
] as const;

export function anthropicSupportsTopEffort(model: string): boolean {
    return ANTHROPIC_TOP_EFFORT_MODELS.some((m) => model.includes(m));
}

export function anthropicSupportsBudgetThinking(model: string): boolean {
    // Sonnet 4.6 straddles the generations: adaptive-capable, but it still accepts
    // budget thinking (see the generation table above) — so an operator's thinkingBudget
    // must not be dropped for it.
    if (isAnthropicAdaptiveThinkingModel(model) && !model.includes('claude-sonnet-4-6')) return false;
    return (
        model.includes('claude-3-7') ||
        model.includes('claude-sonnet-4') ||
        model.includes('claude-opus-4') ||
        model.includes('claude-haiku-4')
    );
}

/**
 * Whether the model accepts the `thinking` field at all (any of enabled/disabled). Everything
 * from Claude 3.7 on does — including the adaptive generation, where `{type:'disabled'}` MUST
 * still be sent to turn reasoning off (models like Sonnet 5 default thinking ON, so dropping an
 * operator's budget-0 would silently leave reasoning enabled).
 */
export function anthropicSupportsThinkingField(model: string): boolean {
    return isAnthropicAdaptiveThinkingModel(model) || anthropicSupportsBudgetThinking(model);
}

/**
 * The effort an Anthropic model actually receives for a configured effort, following the server's
 * request builder: models that predate `output_config.effort` receive none, and the rest map the
 * shared ladder onto their own (`mapReasoningEffortToAnthropic`).
 */
export function resolveAnthropicReasoningEffort(
    model: string,
    effort: ReasoningEffort | null | undefined,
): AnthropicEffort | undefined {
    if (!effort || !anthropicSupportsEffort(model)) return undefined;
    return mapReasoningEffortToAnthropic(effort, model);
}

/**
 * Whether a model accepts the sampling parameters `temperature` and `topP`. OpenAI reasoning
 * models (the o-series and `gpt-5` onwards) and Gemini 3.5+ reject them, so the server strips
 * them before the request and the prompt editor flags a value set for such a model as unused.
 */
export function modelSupportsSamplingParameters(provider: string, model: string): boolean {
    if (provider === 'OPENAI') return !/^(?:o\d|gpt-(?:[5-9]|[1-9]\d))/i.test(model);
    if (provider === 'GOOGLE') return !/^gemini-(?:3\.[5-9]|[4-9]\d*(?:\.|$))/i.test(model);
    return true;
}

/**
 * Claude models whose provider drops `temperature` and `topP` from every request. Mirrors
 * `rejectsSamplingParameters` in the installed `@ai-sdk/anthropic` capability table. Separately,
 * the provider also drops them from any Claude request with thinking enabled.
 */
const ANTHROPIC_SAMPLING_REJECTING_MODELS = [
    'claude-opus-5',
    'claude-fable-5',
    'claude-sonnet-5',
    'claude-opus-4-8',
    'claude-opus-4-7',
] as const;

export function anthropicRejectsSamplingParameters(model: string): boolean {
    return ANTHROPIC_SAMPLING_REJECTING_MODELS.some((m) => model.includes(m));
}

/**
 * Operator-facing labels for the effort ladder. Derived labels ('XHIGH' → 'Xhigh') read as
 * typos, so the display text is spelled out here and shared by every effort dropdown.
 *
 * Labels stay neutral about what "default" means — the two dropdowns disagree. The prompt
 * editor defaults to 'medium'; the model-tier drawer treats a CLEARED value as "inherit the
 * tier default", so tagging 'medium' as the default there would be wrong. A surface that has
 * a default annotates its own option.
 */
export const REASONING_EFFORT_LABELS = {
    minimal: 'Minimal',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    xhigh: 'Extra high',
    max: 'Max',
} satisfies Record<ReasoningEffort, string>;

export const REASONING_EFFORT_OPTIONS = Object.values(REASONING_EFFORTS).map((value) => ({
    label: REASONING_EFFORT_LABELS[value],
    value,
}));

/** Type guard for values arriving from the DB column / imported JSON, which are plain strings. */
export function isReasoningEffort(value: unknown): value is ReasoningEffort {
    return typeof value === 'string' && (Object.values(REASONING_EFFORTS) as string[]).includes(value);
}

export { REASONING_EFFORTS as REASONING_EFFORTS_EXPORT_GUARD };

export const AI_OUTPUT_FORMATS = {
    TEXT: 'text',
    MARKDOWN: 'markdown',
    HTML: 'html',
} as const;

export type AiOutputFormat = (typeof AI_OUTPUT_FORMATS)[keyof typeof AI_OUTPUT_FORMATS];

export const AI_OUTPUT_FORMAT_VALUES = Object.values(AI_OUTPUT_FORMATS);

/**
 * AI Model Tiers
 * Capability-based tiers for different AI use cases
 */
export const AI_MODEL_TIERS = [
    'NANO',
    'LIGHTWEIGHT',
    'BALANCED',
    'EXTRACTION',
    'DOCUMENT_EXTRACTOR',
    'REASONING',
    'PREMIUM',
    'EMBEDDING_SMALL',
    'EMBEDDING_LARGE',
] as const;

export type AiModelTier = (typeof AI_MODEL_TIERS)[number];

/**
 * Metadata for each AI model tier including display label, description, and category
 */
export const AI_MODEL_TIER_METADATA: Record<
    AiModelTier,
    { label: string; description: string; category: 'text' | 'embedding' }
> = {
    NANO: {
        label: 'Nano',
        description: 'Ultra-cheap minimal processing tasks',
        category: 'text',
    },
    LIGHTWEIGHT: {
        label: 'Lightweight',
        description: 'Quick, cost-effective tasks',
        category: 'text',
    },
    BALANCED: {
        label: 'Balanced',
        description: 'Standard quality for most tasks',
        category: 'text',
    },
    EXTRACTION: {
        label: 'Extraction',
        description: 'Cost-effective document metadata extraction',
        category: 'text',
    },
    DOCUMENT_EXTRACTOR: {
        label: 'Document Extractor',
        description: 'Document to markdown conversion using vision models',
        category: 'text',
    },
    REASONING: {
        label: 'Reasoning',
        description: 'Complex analysis and decision-making',
        category: 'text',
    },
    PREMIUM: {
        label: 'Premium',
        description: 'Maximum quality for critical tasks',
        category: 'text',
    },
    EMBEDDING_SMALL: {
        label: 'Embedding (Small)',
        description: 'Cost-effective vector search',
        category: 'embedding',
    },
    EMBEDDING_LARGE: {
        label: 'Embedding (Large)',
        description: 'High-fidelity document analysis',
        category: 'embedding',
    },
} as const;

/**
 * Provider color mappings for UI display
 */
export const AI_PROVIDER_COLORS: Record<string, string> = {
    OPENAI: 'blue',
    GOOGLE: 'green',
    ANTHROPIC: 'purple',
    ZEROENTROPY: 'orange',
    COHERE: 'geekblue',
} as const;
